export interface DashboardWidget {
  id: string;
  name: string;
  description: string;
  defaultEnabled: boolean;
  category: "stats" | "records" | "health" | "actions";
  size: "small" | "medium" | "large";
}

export const availableWidgets: DashboardWidget[] = [
  {
    id: "stats-total",
    name: "Total Records",
    description: "Shows the total number of health records",
    defaultEnabled: true,
    category: "stats",
    size: "small",
  },
  {
    id: "stats-normal",
    name: "Normal Results",
    description: "Shows count of records with normal status",
    defaultEnabled: true,
    category: "stats",
    size: "small",
  },
  {
    id: "stats-attention",
    name: "Needs Attention",
    description: "Shows count of records requiring attention",
    defaultEnabled: true,
    category: "stats",
    size: "small",
  },
  {
    id: "stats-thisMonth",
    name: "This Month",
    description: "Shows count of records from current month",
    defaultEnabled: true,
    category: "stats",
    size: "small",
  },
  {
    id: "health-concerns",
    name: "Health Concerns",
    description: "Track active health concerns and patient-reported symptoms",
    defaultEnabled: true,
    category: "health",
    size: "large",
  },
  {
    id: "health-overview",
    name: "Health Overview",
    description: "Pie chart showing breakdown of record types",
    defaultEnabled: true,
    category: "health",
    size: "medium",
  },
  {
    id: "attention-items",
    name: "Items Needing Attention",
    description: "List of records that need review",
    defaultEnabled: true,
    category: "health",
    size: "medium",
  },
  {
    id: "vital-signs-summary",
    name: "Vital Signs Summary",
    description: "Latest blood pressure, weight, and BMI",
    defaultEnabled: true,
    category: "health",
    size: "medium",
  },
  {
    id: "upcoming-appointments",
    name: "Upcoming Appointments",
    description: "Shows scheduled healthcare appointments",
    defaultEnabled: true,
    category: "records",
    size: "medium",
  },
  {
    id: "medications-summary",
    name: "Current Medications",
    description: "Quick view of active prescriptions",
    defaultEnabled: true,
    category: "health",
    size: "medium",
  },
  {
    id: "quick-actions",
    name: "Quick Actions",
    description: "Shortcuts to upload records and more",
    defaultEnabled: true,
    category: "actions",
    size: "large",
  },
  {
    id: "lab-results-summary",
    name: "Recent Lab Results",
    description: "Summary of latest laboratory tests",
    defaultEnabled: true,
    category: "health",
    size: "medium",
  },
  {
    id: "insurance-summary",
    name: "Insurance Overview",
    description: "Deductible progress and coverage info",
    defaultEnabled: false,
    category: "records",
    size: "medium",
  },
];

export interface DashboardConfig {
  enabledWidgets: string[];
  widgetOrder: string[];
  layout: "default" | "compact" | "expanded";
  version: number;
}

const DASHBOARD_CONFIG_VERSION = 2;

export const defaultDashboardConfig: DashboardConfig = {
  enabledWidgets: availableWidgets.filter(w => w.defaultEnabled).map(w => w.id),
  widgetOrder: availableWidgets.filter(w => w.defaultEnabled).map(w => w.id),
  layout: "default",
  version: DASHBOARD_CONFIG_VERSION,
};

export function saveDashboardConfig(config: DashboardConfig): void {
  if (typeof window !== "undefined") {
    localStorage.setItem("medsynciq-dashboard-config", JSON.stringify(config));
  }
}

export function loadDashboardConfig(): DashboardConfig {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem("medsynciq-dashboard-config");
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Partial<DashboardConfig>;
        if (parsed.version !== DASHBOARD_CONFIG_VERSION) {
          return {
            ...defaultDashboardConfig,
            ...parsed,
            enabledWidgets: Array.from(new Set([
              ...(parsed.enabledWidgets ?? []),
              "lab-results-summary",
            ])),
            widgetOrder: Array.from(new Set([
              ...(parsed.widgetOrder ?? []),
              "lab-results-summary",
            ])),
            version: DASHBOARD_CONFIG_VERSION,
          };
        }
        return parsed as DashboardConfig;
      } catch {
        return defaultDashboardConfig;
      }
    }
  }
  return defaultDashboardConfig;
}
